'use client';

import { useState, type ReactNode } from 'react';
import {
  Check,
  ChevronDown,
  ListPlus,
  Loader2,
  Plus,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useLists } from '@/hooks/use-lists';
import { useSession } from '@/hooks/use-session';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

/**
 * The one way into a list from anywhere else in the product.
 *
 * Until this existed, companies could only be added from inside a list —
 * from its own typeahead or the import wizard — so the whole search surface,
 * with its 37 filters, had no path to the feature it feeds.
 *
 * Creating a list is offered in the same popover as picking one, because the
 * first time anyone uses this they have no lists yet, and bouncing them to
 * /my-lists to make one loses both the company and the intent. The new list
 * receives the companies immediately.
 *
 * `euIds` is whatever the caller has selected: one company from a detail
 * page, or a page of search results. The component does not care which.
 */
export function AddToListMenu({
  euIds,
  variant = 'outline',
  size = 'sm',
  className,
  label,
  onAdded,
}: {
  euIds: string[];
  variant?: 'outline' | 'primary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Overrides the default "Add to list" / "Add N to list" wording. */
  label?: ReactNode;
  onAdded?: (listId: number, added: number) => void;
}) {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const { lists, isLoading, create, isCreating } = useLists();

  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [busyListId, setBusyListId] = useState<number | null>(null);
  const [doneListId, setDoneListId] = useState<number | null>(null);

  const count = euIds.length;

  const commit = async (listId: number, listName: string) => {
    setBusyListId(listId);
    try {
      const res = await fetch(`/api/lists/${listId}/companies/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eu_ids: euIds }),
      });
      if (!res.ok) throw new Error(`bulk add returned ${res.status}`);
      const { data } = (await res.json()) as {
        data: { added: number; already_in_list: number };
      };

      setDoneListId(listId);
      void queryClient.invalidateQueries({ queryKey: ['lists'] });
      onAdded?.(listId, data.added);
      toast.success(
        data.added === 0
          ? t('lists.addTo.allPresent', {
              list: listName,
              defaultValue: 'Already in “{{list}}”',
            })
          : t('lists.addTo.added', {
              count: data.added,
              list: listName,
              defaultValue: '{{count}} added to “{{list}}”',
            }),
      );
      // Leave the tick visible for a moment so the click is acknowledged
      // before the popover disappears.
      setTimeout(() => {
        setOpen(false);
        setDoneListId(null);
      }, 700);
    } catch {
      toast.error(t('lists.addTo.failed', 'Could not add to the list.'));
    } finally {
      setBusyListId(null);
    }
  };

  const createAndCommit = async () => {
    const name = newName.trim();
    if (!name) return;
    const result = await create(name);
    setCreating(false);
    setNewName('');
    await commit(result.id, result.name);
  };

  if (!session) return null;

  const triggerLabel =
    label ??
    (count > 1
      ? t('lists.addTo.buttonMany', {
          count,
          defaultValue: 'Add {{count}} to list',
        })
      : t('lists.addTo.button', 'Add to list'));

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setCreating(false);
            setNewName('');
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant={variant}
            size={size}
            className={className}
            disabled={count === 0}
          >
            <ListPlus className="size-3.5 shrink-0" />
            {triggerLabel}
            <ChevronDown className="size-3 shrink-0 opacity-70" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-1.5">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {lists.length > 0 && (
                <div className="max-h-64 overflow-y-auto">
                  {lists.map((list) => (
                    <button
                      key={list.id}
                      type="button"
                      disabled={busyListId != null}
                      onClick={() => void commit(list.id, list.name)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm',
                        'hover:bg-muted/60 disabled:opacity-60',
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">{list.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {list.company_count}
                      </span>
                      {busyListId === list.id ? (
                        <Loader2 className="size-3.5 shrink-0 animate-spin" />
                      ) : doneListId === list.id ? (
                        <Check className="size-3.5 shrink-0 text-primary" />
                      ) : null}
                    </button>
                  ))}
                </div>
              )}

              {lists.length > 0 && <div className="my-1 border-t border-border" />}

              {creating ? (
                <div className="flex items-center gap-1.5 p-1">
                  <Input
                    autoFocus
                    value={newName}
                    maxLength={255}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void createAndCommit();
                      }
                      if (e.key === 'Escape') setCreating(false);
                    }}
                    placeholder={t('lists.addTo.newNamePlaceholder', 'List name')}
                    className="h-8 text-sm"
                  />
                  <Button
                    size="sm"
                    className="h-8 shrink-0"
                    disabled={!newName.trim() || isCreating || busyListId != null}
                    onClick={() => void createAndCommit()}
                  >
                    {isCreating || busyListId != null ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      t('lists.addTo.create', 'Create')
                    )}
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm hover:bg-muted/60"
                >
                  <Plus className="size-3.5 shrink-0 text-muted-foreground" />
                  {lists.length === 0
                    ? t('lists.addTo.createFirst', 'Create your first list')
                    : t('lists.addTo.createNew', 'New list…')}
                </button>
              )}
            </>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}
